import type { DesignationStyle, DesignationStyleMap, RGBAColor } from "./types";
import { groupBy, isEmpty, negate } from "lodash";
import { ENVIRONMENT } from "src/js/util/environment";

const isNotEmpty = negate(isEmpty);

export function generateColor([r, g, b, a]: RGBAColor, multiplier: number) {
  const n = 255 / Math.max(r, g, b);
  return [
    Math.floor(r * multiplier * n),
    Math.floor(g * multiplier * n),
    Math.floor(b * multiplier * n),
    a,
  ] as RGBAColor;
}

function isRGBAColor(input: unknown): input is RGBAColor {
  return Array.isArray(input) && input.length >= 3 && input.length <= 4;
}

/**
 * Input:
 * {
 *    "key1, key2": [255, 0, 0],
 *    "key3": { fillColor: [0, 255, 0], fillPattern: "hatch-2x" },
 * }
 * Output:
 * {
 *    "key1": { fillColor: [255, 0, 0] },
 *    "key2": { fillColor: [255, 0, 0] },
 *    "key3": { fillColor: [0, 255, 0], fillPattern: "hatch-2x" },
 * }
 */
export function createStyleMap(
  styleMap: Record<string, DesignationStyle | RGBAColor>
): DesignationStyleMap {
  const styleEntries = Object.entries(styleMap);
  const styleEntriesFlattened = styleEntries.flatMap(([keys, style]) =>
    keys
      .split(",")
      .map((k) => k.trim())
      .filter(isNotEmpty)
      .map((k) => [k, style])
  );

  if (/local/i.test(ENVIRONMENT.APP_ENV)) {
    Object.entries(groupBy(styleEntriesFlattened, ([key]) => key))
      .filter(([, value]) => value.length > 1)
      .forEach(([key]) => {
        console.warn("Duplicate style key:", key);
      });
  }

  return Object.fromEntries(
    styleEntriesFlattened.map(([key, style]) => [
      key,
      isRGBAColor(style) ? { fillColor: style } : style,
    ])
  );
}
